/**
 * Authenticated fetch helper
 * Automatically adds bearer token from Supabase session to API requests
 * 
 * This ensures mobile web wrappers (Median, Capacitor, etc.) can authenticate
 * even when cookies don't work reliably.
 */

import { supabase, refreshSessionWithLock } from "@/lib/supabase/client";
import { logDiagnostic, detectEnvironment } from "@/lib/debug/mobile-diagnostics";
import { getApiUrl } from "./utils";

export interface AuthFetchOptions extends RequestInit {
  /**
   * Whether to include credentials (cookies) in the request
   * Default: true (for browser compatibility)
   */
  includeCredentials?: boolean;
}

/**
 * Detect if we should use Bearer token authentication
 * CRITICAL: Use tokens for ALL mobile devices, not just webviews
 * Mobile browsers (especially in webview contexts like Median) may have cookie issues
 */
function shouldUseBearerToken(): boolean {
  if (typeof window === "undefined") return false;
  
  const ua = navigator.userAgent;
  
  // Detect webview wrappers specifically (Capacitor, Cordova, etc.)
  const isWebview = 
    /Cordova|WebView|Capacitor|Ionic/i.test(ua) ||
    window.location.protocol === "capacitor:" ||
    window.location.protocol === "file:" ||
    window.location.protocol === "ionic:" ||
    window.location.origin.includes("capacitor://") ||
    window.location.origin.includes("file://") ||
    window.location.origin.includes("ionic://") ||
    (!window.location.origin.startsWith("http://") && !window.location.origin.startsWith("https://"));
  
  // CRITICAL: Also detect mobile devices (iOS/Android) - cookies may not work reliably
  // This is especially important for Capacitor webview and mobile Safari
  const isMobileDevice = 
    /iPhone|iPad|iPod|Android|Mobile/i.test(ua) ||
    (window.innerWidth < 768 && /Mobile/i.test(ua));
  
  // Use Bearer token if we're in a webview OR on a mobile device
  return isWebview || isMobileDevice;
}

export async function authFetch(
  url: string,
  options: AuthFetchOptions = {}
): Promise<Response> {
  const env = detectEnvironment();
  const shouldUseToken = shouldUseBearerToken();
  
  logDiagnostic("authFetch", "Starting authenticated fetch", {
    url,
    isMobile: env.isMobile,
    isIOS: env.isIOS,
    isTestFlight: env.isTestFlight,
    shouldUseToken,
  });
  
  // Get the current Supabase session
  let accessToken: string | null = null;
  
  // CRITICAL: Get token and add Authorization header if we're on mobile/webview
  // Desktop web should rely on cookies only (existing behavior)
  // OPTIMIZED: Removed refreshSession() and redundant syncSession() calls - just get session directly
  if (shouldUseToken) {
    try {
      // Get session directly - no refresh, no sync (much faster)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (session?.access_token) {
        accessToken = session.access_token;
        logDiagnostic("authFetch", "Session retrieved successfully", {
          hasAccessToken: !!accessToken,
          userId: session.user?.id,
        });
      } else if (sessionError) {
        // Only sync if we get an error AND it's a token expiration error
        const isTokenExpired = sessionError.message?.includes("expired") || 
                               sessionError.message?.includes("Invalid Refresh Token");
        
        if (isTokenExpired) {
          logDiagnostic("authFetch", "Token expired - attempting refresh", {
            error: sessionError.message,
          });
          // Only refresh if token is actually expired
          const { data: { session: refreshedSession } } = await refreshSessionWithLock();
          accessToken = refreshedSession?.access_token || null;
        } else {
          logDiagnostic("authFetch", "Session error (non-expired)", {
            error: sessionError.message,
          });
          // Continue without token - cookies might still work
        }
      }
    } catch (error) {
      logDiagnostic("authFetch", "Exception getting session", {
        error: error instanceof Error ? error.message : String(error),
      });
      console.warn("[authFetch] Failed to get session:", error);
      // Continue without token - cookies might still work
    }
    
    logDiagnostic("authFetch", "Final token status (mobile/webview)", {
      hasAccessToken: !!accessToken,
      tokenLength: accessToken?.length || 0,
    });
  } else {
    logDiagnostic("authFetch", "Desktop web — attach Bearer when JS session exists; cookies remain as fallback");
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (session?.access_token) {
        accessToken = session.access_token;
      } else if (sessionError) {
        logDiagnostic("authFetch", "Desktop getSession note (non-fatal)", {
          error: sessionError.message,
        });
      }
    } catch (error) {
      logDiagnostic("authFetch", "Desktop getSession exception (non-fatal)", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  // MOBILE ONLY: Wait for access token before making the request
  if (shouldUseToken && env.isMobile && !accessToken) {
    const checkpoints = [0, 300, 800, 1500, 2000];

    for (const delay of checkpoints) {
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        accessToken = session.access_token;
        logDiagnostic("authFetch", "Token became available after wait", {
          waitTime: delay,
        });
        break;
      }
    }

    logDiagnostic("authFetch", "Token status after wait", {
      isMobile: env.isMobile,
      shouldUseToken,
      hasAccessToken: !!accessToken,
    });

    if (!accessToken) {
      // Do not hard-fail login/request flow here. On some mobile wrappers, token
      // restoration can lag while cookie auth still succeeds.
      logDiagnostic("authFetch", "Token unavailable after wait; continuing with cookie auth fallback", {
        isMobile: env.isMobile,
        shouldUseToken,
      });
    }
  }

  // Build headers
  const headers = new Headers(options.headers);

  // Attach Bearer whenever we have a token (mobile wrappers + desktop when local session exists).
  // Desktop often has localStorage session before/without synced cookies; server checks Bearer first.
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
    logDiagnostic("authFetch", "Added bearer token to request", {
      context: shouldUseToken ? "mobile-webview" : "desktop-session",
    });
  } else if (shouldUseToken) {
    logDiagnostic("authFetch", "Mobile/webview context but no bearer token — relying on cookies");
  } else {
    logDiagnostic("authFetch", "Desktop — no in-memory session token; relying on cookies only");
  }

  // Ensure Content-Type is set for JSON requests
  if (options.body && typeof options.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // CRITICAL: Always use no-store for authenticated requests to prevent caching
  // This ensures we never cache 401/403 responses or stale authenticated data
  if (!options.cache) {
    options.cache = "no-store";
  }
  
  // Merge with existing headers
  const mergedOptions: RequestInit = {
    ...options,
    headers,
    // Include credentials by default for cookie-based auth
    credentials: options.includeCredentials !== false ? "include" : options.credentials,
    // Ensure cache is set to no-store for all authenticated requests
    cache: options.cache || "no-store",
  };

  // CRITICAL: Use relative URLs for same-origin requests to ensure cookies are sent
  // Only convert to absolute URLs when we're in a web wrapper or truly cross-origin
  // This is essential for production where cookies need to work properly
  let finalUrl = url;
  
  // Check if we're in a web wrapper or need absolute URLs
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    
    // Use the same web wrapper detection logic as getApiBaseUrl() to ensure consistency
    // This includes user agent detection for Capacitor, which is critical when
    // Capacitor loads from a standard https:// URL (not capacitor://)
    const isWebWrapper = 
      protocol === "capacitor:" ||
      protocol === "file:" ||
      protocol === "ionic:" ||
      origin.includes("capacitor://") ||
      origin.includes("file://") ||
      origin.includes("ionic://") ||
      // Detect localhost with non-standard port (common in web wrappers)
      (hostname === "localhost" && port !== "" && port !== "3000" && port !== "3001") ||
      // Detect if origin doesn't match a standard web domain pattern
      (!origin.startsWith("http://") && !origin.startsWith("https://")) ||
      // Check user agent for web wrapper indicators (CRITICAL for Capacitor apps)
      userAgent.includes("Electron") ||
      userAgent.includes("Capacitor") ||
      // If we're on localhost but the app URL env var is set to production, we're likely in a wrapper
      (hostname === "localhost" && process.env.NEXT_PUBLIC_APP_URL && !process.env.NEXT_PUBLIC_APP_URL.includes("localhost"));
    
    // Only use absolute URLs when cross-origin. Use relative URLs when we're on the same origin
    // so cookies are always sent (fixes messages/matches not loading on production website).
    const appUrl = process.env.NEXT_PUBLIC_APP_URL?.trim().replace(/\/+$/, "") || "";
    const isSameOriginAsApp = appUrl && origin === appUrl;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      finalUrl = url; // Already absolute
    } else if (isSameOriginAsApp) {
      // Same origin as app URL (e.g. production site) – use relative URL so cookies are sent
      finalUrl = url.startsWith("/") ? url : `/${url}`;
    } else if (isWebWrapper || process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_APP_URL) {
      finalUrl = getApiUrl(url); // Convert to absolute for web wrappers or when env vars are set
    } else {
      // Same-origin: use relative URL to ensure cookies are sent
      finalUrl = url.startsWith("/") ? url : `/${url}`;
    }
  } else {
    // Server-side: use getApiUrl
    finalUrl = url.startsWith("http://") || url.startsWith("https://") ? url : getApiUrl(url);
  }

  logDiagnostic("authFetch", "Making request", {
    originalUrl: url,
    finalUrl,
    isAbsolute: finalUrl.startsWith("http://") || finalUrl.startsWith("https://"),
    hasAuthHeader: headers.has("Authorization"),
    credentials: mergedOptions.credentials,
  });

  // Add timeout to prevent hanging requests (critical for login flow)
  // Profile fetches and notifications may need longer timeout, especially on mobile/slow networks
  // CRITICAL: Increase timeouts for mobile to prevent premature timeouts
  // Include /api/profiles (list) and /api/profiles/ (single) - list uses ?params so needs url.includes('/api/profiles')
  const isProfileFetch = url.includes("/api/profiles") || url.includes("/api/profile/");
  const isNotificationFetch = url.includes("/api/notifications");
  const isLeaderboardFetch = url.includes("/api/leaderboard");
  const isCommunityFetch = url.includes("/api/communities");
  const isEventsFetch = url.includes("/api/events");
  const isEntitlementsFetch = url.includes("/api/me/entitlements");
  const isMatchesFetch = url.includes("/api/matches");
  const isLikesYouFetch = url.includes("/api/likes-you");
  const isMatchScoreFetch = url.includes("/api/ai/match-score");
  const isGenerateIntroFetch = url.includes("/api/ai/generate-intro");
  
  // Use longer timeouts for mobile/webview environments
  const baseTimeout = shouldUseToken ? 90000 : 45000; // 90s mobile/webview, 45s desktop
  const usesBaseTimeout =
    isProfileFetch ||
    isNotificationFetch ||
    isLeaderboardFetch ||
    isCommunityFetch ||
    isEventsFetch ||
    isEntitlementsFetch ||
    isMatchesFetch ||
    isLikesYouFetch;
  const timeoutMs = isMatchScoreFetch
    ? Math.max(baseTimeout, 120000)
    : isGenerateIntroFetch
      ? Math.max(baseTimeout, 90000)
      : usesBaseTimeout
        ? baseTimeout
        : 30000;
  const timeoutController = new AbortController();
  let didTimeout = false;
  const timeoutId = setTimeout(() => {
    didTimeout = true;
    // Do not pass Error to abort(): devtools/Next overlay attribute the stack to
    // this line; we classify timeout via didTimeout and throw a user-facing message below.
    timeoutController.abort();
  }, timeoutMs);

  // Merge abort signals if one already exists
  const existingSignal = mergedOptions.signal;
  let finalSignal = timeoutController.signal;
  let cleanupAbortListeners: (() => void) | null = null;

  if (existingSignal) {
    const mergedController = new AbortController();

    const abortFromSignal = (signal: AbortSignal, fallbackMessage: string) => {
      if (mergedController.signal.aborted) return;
      const reason = (signal as any).reason;
      mergedController.abort(reason ?? new Error(fallbackMessage));
    };

    const handleExistingAbort = () => abortFromSignal(existingSignal, "Request was cancelled.");
    const handleTimeoutAbort = () =>
      abortFromSignal(timeoutController.signal, `Request timed out after ${timeoutMs}ms`);

    if (existingSignal.aborted) {
      handleExistingAbort();
    } else {
      existingSignal.addEventListener("abort", handleExistingAbort, { once: true });
    }

    if (timeoutController.signal.aborted) {
      handleTimeoutAbort();
    } else {
      timeoutController.signal.addEventListener("abort", handleTimeoutAbort, { once: true });
    }

    cleanupAbortListeners = () => {
      existingSignal.removeEventListener("abort", handleExistingAbort);
      timeoutController.signal.removeEventListener("abort", handleTimeoutAbort);
    };

    finalSignal = mergedController.signal;
  }

  try {
    // Make the request with resolved URL and timeout
    const response = await fetch(finalUrl, {
      ...mergedOptions,
      signal: finalSignal,
    });
    
    clearTimeout(timeoutId);
    cleanupAbortListeners?.();
  
  logDiagnostic("authFetch", "Request completed", {
    url: finalUrl,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
  });
  
  return response;
  } catch (error) {
    clearTimeout(timeoutId);
    cleanupAbortListeners?.();
    
    // Only classify as timeout when our timeout controller actually fired.
    const isAbortError =
      (error instanceof Error && error.name === "AbortError") ||
      (error instanceof Error && error.name === "TimeoutError") ||
      (error instanceof Error && error.message.toLowerCase().includes("aborted"));

    if (isAbortError) {
      if (!didTimeout && !timeoutController.signal.aborted) {
        const abortReason = (finalSignal as any)?.reason;
        const reasonMessage =
          typeof abortReason === "string"
            ? abortReason
            : abortReason instanceof Error
              ? abortReason.message
              : "Request aborted.";
        throw new DOMException(reasonMessage || "Request aborted.", "AbortError");
      }

      logDiagnostic("authFetch", "Request timed out", {
        url: finalUrl,
        timeoutMs,
      });
      throw new Error(`Request timed out after ${timeoutMs}ms. Please check your connection and try again.`);
    }
    
    throw error;
  }
}

/**
 * Make an authenticated API request and parse JSON response
 * 
 * @param url - API endpoint URL
 * @param options - Fetch options
 * @returns Promise with parsed JSON data
 */
export async function authFetchJson<T = any>(
  url: string,
  options: AuthFetchOptions = {},
  retryOnAuth = true
): Promise<T> {
  const env = detectEnvironment();
  const isMobile = env.isMobile;
  
  let response: Response;
  try {
    response = await authFetch(url, options);
  } catch (error) {
    // STOP INFINITE RETRIES: If authFetch throws on mobile, do NOT retry automatically
    // Let login fail once instead of looping forever
    if (isMobile && error instanceof Error && error.message.includes("Mobile auth failed")) {
      throw error;
    }
    throw error;
  }
  
  if (!response.ok) {
    // Try to parse error response
    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
    }

    // DEFERRED PHONE (Stage 2): the server gate on outbound actions (message,
    // connect, post) returns 403 + code "phone_verification_required". Bounce the
    // user to the just-in-time verify page, preserving where they were, then stop.
    if (response.status === 403 && errorData?.code === "phone_verification_required") {
      if (typeof window !== "undefined") {
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = `/verify-phone?returnTo=${encodeURIComponent(returnTo)}`;
      }
      const error = new Error(errorData.error || "Verify your phone number to continue.");
      (error as any).status = 403;
      (error as any).data = errorData;
      throw error;
    }

    // STOP INFINITE RETRIES: Do NOT retry on mobile if authFetch already failed
    // For 401 errors, retry once after syncing session (handles session restoration timing)
    // BUT: Skip retry on mobile to prevent infinite loops
    if (response.status === 401 && retryOnAuth && !isMobile) {
      console.warn("[authFetchJson] 401 error - attempting session sync and retry");
      
      try {
        // Sync session and wait a bit for it to be ready (reduced from 500ms to 300ms)
        const { useAuthStore } = await import("@/stores/auth-store");
        await useAuthStore.getState().syncSession();
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Verify session is now available
        const { supabase } = await import("@/lib/supabase/client");
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.access_token) {
          console.log("[authFetchJson] Session restored, retrying request");
          // Retry the request once (with retry disabled to prevent infinite loops)
          return authFetchJson<T>(url, options, false);
        }
      } catch (retryError) {
        console.warn("[authFetchJson] Session sync/retry failed:", retryError);
        // Fall through to throw the original error
      }
    }
    
    // For 401 errors, provide more helpful error message
    if (response.status === 401) {
      const error = new Error(errorData.error || "Authentication required. Please log in and try again.");
      (error as any).status = response.status;
      (error as any).data = errorData;
      (error as any).isAuthError = true;
      throw error;
    }
    
    // Provide more descriptive error messages
    const errorMessage = errorData.error || errorData.message || `Request failed: ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).status = response.status;
    (error as any).data = errorData;
    throw error;
  }
  
  // Guard against HTML responses (404 pages, auth redirects, error pages)
  const contentType = response.headers.get("Content-Type") || "";
  const text = await response.text();
  if (!contentType.includes("application/json")) {
    const preview = text.slice(0, 80).replace(/\s+/g, " ");
    throw new Error(
      `Expected JSON but received ${contentType || "unknown"}. ` +
      `Response starts with: "${preview}...". ` +
      `Check that the API route exists and returns JSON.`
    );
  }
  return JSON.parse(text) as T;
}
